import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Receipt,
  CalendarClock,
  Users,
  LayoutDashboard,
  ArrowRight,
  AlertCircle,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { Pill } from '../components/ui/pill.js';
import { api, type SalesOrderSummary, type SpireHealth } from '../lib/api.js';
import {
  FOP_UDF,
  readAssignment,
  readCompletedAt,
  todayLocalIso,
} from '../lib/dispatch-board.js';
import type { FieldPhase } from '../lib/api.js';

/**
 * Today / office dashboard.
 *
 * Pulls one batch of open orders (with UDF enrichment for placed ones) and
 * decomposes them into today-specific buckets driven by Tinsmith's FOP_*
 * UDFs. Header pill reflects live Spire reachability rather than a static
 * "all systems normal" — a real signal the office can trust on a morning
 * glance.
 */
export function HomeScreen() {
  const todayIso = todayLocalIso();
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  // Spire connectivity — drives the header health pill. Same source the
  // admin panel uses, polled at a longer interval here (office staff don't
  // need 30s freshness on the landing page).
  const healthQuery = useQuery({
    queryKey: ['home', 'spire-health'],
    queryFn: ({ signal }) => api.admin.spireHealth(signal),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  // Pull the whole open-order book (same 500 the dispatch board asks for)
  // so the pipeline panel reflects real Spire state. Spire returns oldest
  // first, so a smaller page silently drops the newest orders — at 200,
  // FPS's 256 open orders hid today's newest jobs here (2026-10-08).
  // `includeUdf=true` is conditional server-side: only orders with
  // FOP_TECH set get the (cached) detail-fetch.
  const ordersQuery = useQuery({
    queryKey: ['home', 'open-orders'],
    queryFn: ({ signal }) =>
      api.salesOrders.search({ status: 'open', includeUdf: true, limit: 500 }, signal),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const orders: ReadonlyArray<SalesOrderSummary> = ordersQuery.data ?? [];

  const breakdown = decomposeForToday(orders, todayIso);
  const quotesTotal = sumDecimal(breakdown.quotes.map((q) => q.total));
  const attentionItems = buildAttentionFeed(orders, todayIso);

  return (
    <div className="space-y-5">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Today</h1>
          <p className="text-sm text-ink-muted">{today}</p>
        </div>
        <HealthPill data={healthQuery.data} loading={healthQuery.isPending} />
      </header>

      <section aria-label="Today KPIs">
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <KpiTile
            label="Scheduled today"
            value={ordersQuery.isPending ? '…' : String(breakdown.scheduledToday.length)}
            sub={`${breakdown.unscheduled.length} still unassigned`}
            href="/operations"
          />
          <KpiTile
            label="In progress"
            value={ordersQuery.isPending ? '…' : String(breakdown.inProgress.length)}
            sub={breakdown.inProgress.length > 0 ? 'techs are on the clock' : 'no active visits'}
            href="/operations"
            tone={breakdown.inProgress.length > 0 ? 'live' : 'neutral'}
          />
          <KpiTile
            label="Completed today"
            value={ordersQuery.isPending ? '…' : String(breakdown.completedToday.length)}
            sub="visits closed out"
            href="/operations"
            tone="success"
          />
          <KpiTile
            label="Open quotes"
            value={ordersQuery.isPending ? '…' : String(breakdown.quotes.length)}
            sub={`$${formatThousands(quotesTotal)} pipeline`}
            href="/operations?status=quotes"
          />
        </div>
      </section>

      <section aria-label="Pipeline" className="grid gap-3 lg:grid-cols-2">
        <PipelinePanel breakdown={breakdown} loading={ordersQuery.isPending} />
        <AttentionPanel items={attentionItems} loading={ordersQuery.isPending} />
      </section>

      <section aria-label="Quick actions">
        <h2 className="text-sm font-medium text-ink-muted mb-2">Quick actions</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ActionCard to="/inventory" Icon={Box} title="Inventory lookup" subtitle="Live stock and customer-specific pricing" />
          <ActionCard to="/quote-builder" Icon={Receipt} title="New quote" subtitle="Build, sign, submit to Spire" />
          <ActionCard to="/jobs" Icon={CalendarClock} title="My day" subtitle="Field tech my-day flow" />
          <ActionCard to="/operations" Icon={LayoutDashboard} title="Operations" subtitle="Inbox + dispatch board" />
          <ActionCard to="/customers" Icon={Users} title="Customers" subtitle="Accounts, credit, contacts" />
        </div>
      </section>
    </div>
  );
}

/**
 * Today buckets — pure decomposition of the orders list keyed off FOP_*
 * UDFs. Same FieldPhase semantics the dispatch board uses; an order
 * counts as "in progress" when the open TimeEntry's phase is anything
 * other than `scheduled` / `signed_off` and FOP_COMPLETED_AT is still
 * empty.
 */
interface TodayBreakdown {
  quotes: ReadonlyArray<SalesOrderSummary>;
  unscheduled: ReadonlyArray<SalesOrderSummary>;
  scheduledToday: ReadonlyArray<SalesOrderSummary>;
  inProgress: ReadonlyArray<SalesOrderSummary>;
  completedToday: ReadonlyArray<SalesOrderSummary>;
}

function decomposeForToday(
  orders: ReadonlyArray<SalesOrderSummary>,
  todayIso: string,
): TodayBreakdown {
  const quotes: SalesOrderSummary[] = [];
  const unscheduled: SalesOrderSummary[] = [];
  const scheduledToday: SalesOrderSummary[] = [];
  const inProgress: SalesOrderSummary[] = [];
  const completedToday: SalesOrderSummary[] = [];

  for (const order of orders) {
    if (order.type === 'quote') {
      quotes.push(order);
      continue;
    }
    const udf = order.udf ?? {};
    const assignment = readAssignment(udf);
    const completedAt = readCompletedAt(udf);
    const phase = readPhase(udf[FOP_UDF.phase]);

    if (completedAt) {
      // Finished work never re-enters the pending pipeline. Completed
      // TODAY counts in the "Completed today" bucket; completed on an
      // earlier day is history — omit entirely rather than letting it
      // fall through to "Scheduled today" and read as still-pending
      // (audit bucket-misfiling finding). Re-dispatching a finished job
      // clears FOP_COMPLETED_AT, so it re-enters the pipeline cleanly.
      if (isToday(completedAt, todayIso)) {
        completedToday.push(order);
      }
      continue;
    }
    if (!assignment) {
      unscheduled.push(order);
      continue;
    }
    if (phase && isActivePhase(phase)) {
      inProgress.push(order);
      continue;
    }
    if (assignment.date === todayIso) {
      scheduledToday.push(order);
    }
    // Orders scheduled on other days don't surface on the Today page —
    // they're visible on /operations via the date picker.
  }

  return { quotes, unscheduled, scheduledToday, inProgress, completedToday };
}

function readPhase(value: string | undefined): FieldPhase | null {
  if (!value) return null;
  switch (value) {
    case 'scheduled':
    case 'traveling':
    case 'on_site':
    case 'work_in_progress':
    case 'paused':
    case 'work_completed':
    case 'signed_off':
      return value;
    default:
      return null;
  }
}

function isActivePhase(phase: FieldPhase): boolean {
  return phase !== 'scheduled' && phase !== 'signed_off';
}

/** True when an ISO timestamp falls on the same local calendar day as todayIso. */
function isToday(at: Date, todayIso: string): boolean {
  const y = at.getFullYear();
  const m = String(at.getMonth() + 1).padStart(2, '0');
  const d = String(at.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}` === todayIso;
}

interface AttentionItem {
  id: string;
  severity: 'warning' | 'info';
  text: string;
  href: string;
}

function buildAttentionFeed(
  orders: ReadonlyArray<SalesOrderSummary>,
  todayIso: string,
): ReadonlyArray<AttentionItem> {
  const items: AttentionItem[] = [];

  // Overdue intake — required date has passed and the job isn't on the
  // board yet. Highest signal for the morning standup; if there are
  // any, the dispatcher should be scheduling these first.
  const overdueUnscheduled = orders.filter((o) => {
    if (o.type !== 'order') return false;
    if (!o.requiredDate) return false;
    if (o.requiredDate >= todayIso) return false;
    const a = readAssignment(o.udf ?? {});
    return !a;
  });
  if (overdueUnscheduled.length > 0) {
    items.push({
      id: 'overdue-unscheduled',
      severity: 'warning',
      text: `${overdueUnscheduled.length} order${overdueUnscheduled.length === 1 ? '' : 's'} past required date — still unscheduled`,
      href: '/operations',
    });
  }

  // Stale quotes — a quote that hasn't been touched in 14+ days is
  // typically dead-in-the-water. Surface so someone follows up or
  // closes it.
  const fourteenDaysAgo = Date.now() - 14 * 86_400_000;
  const staleQuotes = orders.filter((o) => {
    if (o.type !== 'quote') return false;
    if (!o.modifiedAt) return true; // never modified = ancient
    const t = Date.parse(o.modifiedAt);
    return Number.isFinite(t) && t < fourteenDaysAgo;
  });
  if (staleQuotes.length > 0) {
    items.push({
      id: 'stale-quotes',
      severity: 'info',
      text: `${staleQuotes.length} quote${staleQuotes.length === 1 ? '' : 's'} idle for 14+ days`,
      href: '/operations?status=quotes',
    });
  }

  // Scheduled today but unassigned — happens when a dispatcher partially
  // filled the UDF (e.g. set FOP_DATE without FOP_TECH). Shouldn't be
  // common given our UDF helpers always write the full tuple, but worth
  // surfacing if Spire data drifts.
  const todayHole = orders.filter((o) => {
    const udf = o.udf ?? {};
    return (
      udf[FOP_UDF.date]?.trim() === todayIso &&
      !udf[FOP_UDF.tech]?.trim()
    );
  });
  if (todayHole.length > 0) {
    items.push({
      id: 'today-no-tech',
      severity: 'warning',
      text: `${todayHole.length} order${todayHole.length === 1 ? '' : 's'} dated today but no tech assigned`,
      href: '/operations',
    });
  }

  return items;
}

function HealthPill({ data, loading }: { data: SpireHealth | undefined; loading: boolean }) {
  if (loading || !data) return <Pill variant="neutral" size="md">Probing…</Pill>;
  if (!data.reachable) {
    return (
      <Pill variant="danger" dot size="md" title={data.errorMessage ?? 'Spire unreachable'}>
        Spire offline
      </Pill>
    );
  }
  if (data.breakerState === 'open') {
    return (
      <Pill variant="danger" dot size="md" title="Circuit breaker open — paused calls">
        Breaker open
      </Pill>
    );
  }
  if (data.breakerState === 'half_open') {
    return (
      <Pill variant="warning" dot size="md" title="Recovering from a recent failure">
        Recovering
      </Pill>
    );
  }
  return (
    <Pill variant="success" dot size="md" title={`Spire ${data.latencyMs}ms · ${data.company}`}>
      All systems normal
    </Pill>
  );
}

interface PipelineBucket {
  label: string;
  count: number;
  color: 'service' | 'install' | 'pm' | 'sales' | 'emergency' | 'neutral';
  href: string;
}

function PipelinePanel({
  breakdown,
  loading,
}: {
  breakdown: TodayBreakdown;
  loading: boolean;
}) {
  const buckets: ReadonlyArray<PipelineBucket> = [
    { label: 'Quotes', count: breakdown.quotes.length, color: 'sales', href: '/operations?status=quotes' },
    { label: 'Unscheduled', count: breakdown.unscheduled.length, color: 'install', href: '/operations' },
    { label: 'Scheduled today', count: breakdown.scheduledToday.length, color: 'service', href: '/operations' },
    { label: 'In progress', count: breakdown.inProgress.length, color: 'emergency', href: '/operations' },
    { label: 'Completed today', count: breakdown.completedToday.length, color: 'pm', href: '/operations' },
  ];
  const max = Math.max(...buckets.map((b) => b.count), 1);

  return (
    <div
      className="rounded-lg border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-ink">Job pipeline today</div>
        <Link to="/operations" className="text-xs text-ink-accent hover:underline inline-flex items-center gap-1">
          Operations <ArrowRight className="h-3 w-3" aria-hidden />
        </Link>
      </div>
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-6 bg-surface-muted rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <ul className="space-y-2">
          {buckets.map((b) => (
            <li key={b.label}>
              <Link
                to={b.href}
                className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-sm rounded-md px-1.5 py-0.5 -mx-1.5 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
              >
                <span className="text-ink">{b.label}</span>
                <div className="h-2 w-32 rounded-full bg-surface-muted overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(b.count / max) * 100}%`,
                      backgroundColor: pipeColor(b.color),
                    }}
                  />
                </div>
                <span className="text-sm tabular-nums w-8 text-right">{b.count}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AttentionPanel({
  items,
  loading,
}: {
  items: ReadonlyArray<AttentionItem>;
  loading: boolean;
}) {
  return (
    <div
      className="rounded-lg border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-ink">Needs attention</div>
        {!loading && (
          <span className="text-[10.5px] uppercase tracking-wider text-ink-subtle">
            {items.length} item{items.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-8 bg-surface-muted rounded animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-ink-muted py-2 flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-700" aria-hidden />
          You're caught up. Nothing flagged for today.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li key={it.id}>
              <Link
                to={it.href}
                className="flex items-start gap-2 text-sm rounded-md px-2 py-1.5 -mx-2 hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
              >
                <AlertCircle
                  className="h-4 w-4 mt-0.5 shrink-0"
                  style={{
                    color:
                      it.severity === 'warning'
                        ? 'var(--feedback-warning-fg)'
                        : 'var(--feedback-info-fg)',
                  }}
                  aria-hidden
                />
                <span className="text-ink leading-snug">{it.text}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function KpiTile({
  label,
  value,
  sub,
  href,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  href?: string;
  tone?: 'live' | 'success' | 'neutral';
}) {
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-subtle">
        {tone === 'live' && (
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: 'var(--feedback-info-fg)' }}
          />
        )}
        {label}
      </div>
      <div
        className="mt-0.5 text-xl font-semibold tabular-nums"
        style={{
          color:
            tone === 'success'
              ? 'var(--feedback-success-fg)'
              : tone === 'live'
                ? 'var(--feedback-info-fg)'
                : 'var(--color-text-primary)',
        }}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[10px] text-ink-subtle">{sub}</div>}
    </>
  );
  if (!href) {
    return (
      <div
        className="rounded-lg border bg-surface px-3 py-3"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        {body}
      </div>
    );
  }
  return (
    <Link
      to={href}
      className="rounded-lg border bg-surface px-3 py-3 block transition-shadow hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      {body}
    </Link>
  );
}

function ActionCard({
  to,
  Icon,
  title,
  subtitle,
}: {
  to: string;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
}) {
  return (
    <Link
      to={to}
      className="group rounded-lg border bg-surface p-4 transition-all hover:shadow-sm hover:border-line-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: 'var(--pill-service-bg)', color: 'var(--pill-service-fg)' }}
        >
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0">
          <div className="font-medium text-ink">{title}</div>
          <div className="mt-0.5 text-sm text-ink-muted">{subtitle}</div>
        </div>
      </div>
    </Link>
  );
}

function pipeColor(c: 'service' | 'install' | 'pm' | 'sales' | 'emergency' | 'neutral'): string {
  // Maps to --trade-* CSS vars in index.css — light + dark themes both shift
  // these to keep contrast against the canvas. Adding a new trade? Add the
  // CSS var first, then the case here.
  return `var(--trade-${c})`;
}

function sumDecimal(values: ReadonlyArray<string>): string {
  let sum = 0;
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n)) sum += n;
  }
  return sum.toFixed(2);
}

function formatThousands(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
